import { useEffect, useState } from "react";
import { Briefcase, MapPin, Clock, Send, CheckCircle2, Banknote } from "lucide-react";
import { Reveal } from "@/components/Motion";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CATEGORY_ORDER = ["Professional Sales", "Laboratory Assistant", "Warehouse & Logistics", "Legal & Compliance", "Finance & Accounting"];
const APPLY_EMAIL = "jobs.exoplusbio@gmail.com";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

export default function JoinOurTeam() {
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/jobs`)
      .then((r) => r.json())
      .then(setJobs)
      .catch(() => setJobs([]));
  }, []);

  const groups = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    postings: (jobs || []).filter((j) => j.category === cat),
  })).filter((g) => g.postings.length > 0);

  return (
    <div className="pt-16" data-testid="careers-page">
      <section className="border-b border-[#e4e9ec]">
        <div className="mx-auto max-w-[1440px] px-5 pb-14 pt-20 md:px-10 md:pb-16 md:pt-24">
          <Reveal>
            <p className="font-lab text-[11px] uppercase tracking-[0.24em] text-[#0e8c8c]">Careers at Exoplus Bio</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl" data-testid="careers-headline">
              Join Our Team
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#56636b]" data-testid="careers-intro">
              We are growing across Canada. If you are passionate about cosmetic science, careful operations or
              professional sales, we would love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          {jobs === null && (
            <p className="font-lab text-xs text-[#56636b]" data-testid="careers-loading">
              Loading openings…
            </p>
          )}
          {jobs && groups.length === 0 && (
            <p className="text-sm text-[#56636b]" data-testid="careers-empty">
              There are no open postings right now — check back soon.
            </p>
          )}
          {groups.map((group) => {
            const anyOpen = group.postings.some((j) => j.status === "open");
            return (
              <div key={group.category} className="mt-12 first:mt-0" data-testid="careers-category">
                <Reveal>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e4e9ec] pb-3">
                    <h2 className="font-display text-2xl sm:text-3xl">{group.category}</h2>
                    {anyOpen ? (
                      <span
                        className="font-lab border border-[#0e8c8c]/40 bg-[#e3f2f0]/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#0e8c8c]"
                        data-testid="careers-status-open"
                      >
                        Accepting applications
                      </span>
                    ) : (
                      <span
                        className="font-lab border border-[#8a959c]/40 bg-[#f4f3ef] px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#56636b]"
                        data-testid="careers-status-filled"
                      >
                        Filled
                      </span>
                    )}
                  </div>
                </Reveal>
                <div className="mt-6 grid gap-5 lg:grid-cols-2">
                  {group.postings.map((job, i) => (
                    <Reveal key={job.id} delay={i * 0.05}>
                      <div className="flex h-full flex-col border border-[#e4e9ec] bg-white p-6" data-testid={`careers-job-${job.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-display text-xl leading-snug">{job.title}</h3>
                          <Briefcase className="h-5 w-5 shrink-0 text-[#0e8c8c]" />
                        </div>
                        <div className="font-lab mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.14em] text-[#56636b]">
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-[#0e8c8c]" />
                            {job.region}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-[#0e8c8c]" />
                            Posted {fmtDate(job.posted_at)}
                          </span>
                        </div>
                        {job.compensation ? (
                          <p className="font-lab mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#0e8c8c]" data-testid={`careers-comp-${job.id}`}>
                            <Banknote className="h-3.5 w-3.5" /> {job.compensation}
                          </p>
                        ) : null}
                        <p className="mt-4 flex-1 text-sm leading-relaxed text-[#56636b]">{job.description}</p>
                        {job.status === "open" ? (
                          <a
                            href={`mailto:${APPLY_EMAIL}?subject=${encodeURIComponent(`Application — ${job.title}`)}`}
                            data-testid={`careers-apply-${job.id}`}
                            className="mt-6 inline-flex w-fit items-center gap-2 bg-[#0a0a0a] px-6 py-3 text-sm text-white transition-colors hover:bg-[#0e8c8c]"
                          >
                            <Send className="h-4 w-4" /> Apply Now
                          </a>
                        ) : (
                          <span
                            className="font-lab mt-6 inline-flex w-fit items-center gap-2 border border-[#8a959c]/40 bg-[#f4f3ef] px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-[#56636b]"
                            data-testid={`careers-filled-${job.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4" /> Position filled
                          </span>
                        )}
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })}
          {jobs && groups.length > 0 && (
            <Reveal>
              <p
                className="font-lab mt-12 border-l-2 border-[#b4690e] bg-[#b4690e]/5 p-4 text-[10px] leading-relaxed text-[#56636b]"
                data-testid="careers-note"
              >
                Don't see your role? Send your resume to {APPLY_EMAIL} anyway — we review every application and keep
                strong candidates on file. Exoplus Bio products are cosmetics; all roles support cosmetic sales and
                operations only.
              </p>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
